import React from "react";
import { jsPDF } from "jspdf";
import { Code2, Copy, Download, ExternalLink, Key, Package, Server, Smartphone, Wifi } from "lucide-react";
import { API_BASE } from "../utils/api";

export default function ApiDocument({ profile }) {
  const apiKey = profile?.apiKey || "";
  const maskedApiKey = apiKey ? `${apiKey.slice(0, 8)}...${apiKey.slice(-4)}` : "Not generated yet";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadApiDocumentation = () => {
    const docs = `API Documentation\n\nIntegrate your website with our platform to sell data and airtime directly.\n\nGetting Started\n\nBase URL: ${API_BASE}/api\n\nAuthentication\n\nInclude your API key in the Authorization header:\n\nAuthorization: Bearer YOUR_API_KEY\n\nAPI Endpoints\n\nPurchase Data\nPOST /purchase/data\n\nRequest Body:\n{\n  "network": "mtn",\n  "phone": "0501234567",\n  "planId": "plan_123"\n}\n\nResponse:\n{\n  "success": true,\n  "transactionId": "txn_123456",\n  "message": "Data purchase successful"\n}\n\nPurchase Airtime\nPOST /purchase/airtime\n\nRequest Body:\n{\n  "network": "mtn",\n  "phone": "0501234567",\n  "amount": 10\n}\n\nResponse:\n{\n  "success": true,\n  "transactionId": "txn_123456",\n  "message": "Airtime purchase successful"\n}\n\nCheck Balance\nGET /balance\n\nResponse:\n{\n  "success": true,\n  "balance": 150.50,\n  "currency": "GHS"\n}\n\nGet Packages\nGET /packages\n\nResponse:\n{\n  "success": true,\n  "packages": [\n    {\n      "id": "plan_123",\n      "name": "1GB Daily",\n      "network": "mtn",\n      "price": 5.00,\n      "validity": "1 day"\n    }\n  ]\n}\n\nError Handling\n\nAll API responses include a success field. When success is false, an error message is provided.\n\n{\n  "success": false,\n  "error": "Insufficient balance",\n  "code": "INSUFFICIENT_BALANCE"\n}\n\nCommon Error Codes:\n- INSUFFICIENT_BALANCE - Not enough balance for the transaction\n- INVALID_PHONE - Phone number format is invalid\n- INVALID_NETWORK - Network provider not supported\n- PLAN_NOT_FOUND - Data plan ID does not exist\n- UNAUTHORIZED - Invalid or missing API key\n\nDiagnostics\n\nIf your integration returns an HTML page instead of JSON, your configured base URL is likely pointing to the website rather than the API. Use the steps below to validate.\n\nExample diagnostic fetch (client-side):\n\nasync function fetchJson(url, opts) {\n  const res = await fetch(url, opts);\n  const text = await res.text();\n  const ct = res.headers.get('content-type') || '';\n  try {\n    if (ct.includes('application/json')) return JSON.parse(text);\n    throw new Error('Non-JSON response: content-type=' + ct + ' body=' + String(text).slice(0,400));\n  } catch (e) {\n    throw e;\n  }\n}\n\nCode Examples\n\nJavaScript, PHP, and Python usage examples are included in the documentation.`;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const lines = doc.splitTextToSize(docs, 520);
    doc.text(lines, 40, 60);
    doc.save("api-documentation.pdf");
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Code2 size={32} className="text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">API Documentation</h1>
        <p className="mt-2 text-slate-600">
          Integrate your website with our platform to sell data and airtime directly
        </p>
        <div className="mt-4 flex justify-center">
          <button
            onClick={downloadApiDocumentation}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            <Download size={16} />
            Download Docs
          </button>
        </div>
      </div>

      {/* API Key Section */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <Key size={20} className="text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">Your API Key</h2>
        </div>
        <div className="flex items-center gap-3">
          <code className="flex-1 rounded-lg bg-slate-100 px-4 py-3 font-mono text-sm text-slate-800">
            {maskedApiKey}
          </code>
          {apiKey && (
            <button
              onClick={() => copyToClipboard(apiKey)}
              className="rounded-lg bg-slate-900 px-4 py-3 text-white hover:bg-slate-800"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
        {!apiKey && (
          <p className="mt-3 text-sm text-amber-600">
            Contact your administrator to generate an API key for your account.
          </p>
        )}
      </div>

      {/* Getting Started */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Getting Started</h2>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            Our API allows you to integrate data and airtime purchasing directly into your website.
            All transactions are deducted from your agent balance.
          </p>
          <div className="rounded-lg bg-blue-50 p-4">
            <p className="font-medium text-blue-900">Base URL:</p>
            <code className="mt-1 block font-mono text-blue-800">
              https://your-api-domain.com/api
            </code>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Authentication</h2>
        <p className="mb-4 text-sm text-slate-600">
          Include your API key in the Authorization header for all requests:
        </p>
        <div className="rounded-lg bg-slate-100 p-4">
          <code className="font-mono text-sm">
            Authorization: Bearer YOUR_API_KEY
          </code>
        </div>
      </div>

      {/* Endpoints */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-6 text-lg font-semibold text-slate-900">API Endpoints</h2>

        {/* Purchase Data */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Wifi size={20} className="text-green-600" />
            <h3 className="font-semibold text-slate-900">Purchase Data</h3>
          </div>
          <div className="rounded-lg bg-slate-100 p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <code className="font-mono text-sm font-semibold">POST /purchase/data</code>
              <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                Active
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="font-medium text-slate-900 mb-2">Request Body:</p>
              <pre className="rounded-lg bg-slate-100 p-4 text-sm overflow-x-auto">
{`{
  "network": "mtn", // mtn, airteltigo, telecel
  "phone": "0501234567",
  "planId": "plan_123"
}`}
              </pre>
            </div>
            <div>
              <p className="font-medium text-slate-900 mb-2">Response:</p>
              <pre className="rounded-lg bg-green-50 p-4 text-sm overflow-x-auto border border-green-200">
{`{
  "success": true,
  "transactionId": "txn_123456",
  "message": "Data purchase successful"
}`}
              </pre>
            </div>
          </div>
        </div>

        {/* Purchase Airtime */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Smartphone size={20} className="text-blue-600" />
            <h3 className="font-semibold text-slate-900">Purchase Airtime</h3>
          </div>
          <div className="rounded-lg bg-slate-100 p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <code className="font-mono text-sm font-semibold">POST /purchase/airtime</code>
              <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                Active
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="font-medium text-slate-900 mb-2">Request Body:</p>
              <pre className="rounded-lg bg-slate-100 p-4 text-sm overflow-x-auto">
{`{
  "network": "mtn", // mtn, airteltigo, telecel
  "phone": "0501234567",
  "amount": 10 // Amount in GHS
}`}
              </pre>
            </div>
            <div>
              <p className="font-medium text-slate-900 mb-2">Response:</p>
              <pre className="rounded-lg bg-green-50 p-4 text-sm overflow-x-auto border border-green-200">
{`{
  "success": true,
  "transactionId": "txn_123456",
  "message": "Airtime purchase successful"
}`}
              </pre>
            </div>
          </div>
        </div>

        {/* Check Balance */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Server size={20} className="text-purple-600" />
            <h3 className="font-semibold text-slate-900">Check Balance</h3>
          </div>
          <div className="rounded-lg bg-slate-100 p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <code className="font-mono text-sm font-semibold">GET /balance</code>
              <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                Active
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="font-medium text-slate-900 mb-2">Response:</p>
              <pre className="rounded-lg bg-green-50 p-4 text-sm overflow-x-auto border border-green-200">
{`{
  "success": true,
  "balance": 150.50,
  "currency": "GHS"
}`}
              </pre>
            </div>
          </div>
        </div>

        {/* Get Packages */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <Package size={20} className="text-orange-600" />
            <h3 className="font-semibold text-slate-900">Get Packages</h3>
          </div>
          <div className="rounded-lg bg-slate-100 p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <code className="font-mono text-sm font-semibold">GET /packages</code>
              <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                Active
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="font-medium text-slate-900 mb-2">Response:</p>
              <pre className="rounded-lg bg-green-50 p-4 text-sm overflow-x-auto border border-green-200">
{`{
  "success": true,
  "packages": [
    {
      "id": "plan_123",
      "name": "1GB Daily",
      "network": "mtn",
      "price": 5.00,
      "validity": "1 day"
    }
  ]
}`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Error Handling */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Error Handling</h2>
        <p className="mb-4 text-sm text-slate-600">
          All API responses include a success field. When success is false, an error message is provided:
        </p>
        <pre className="rounded-lg bg-red-50 p-4 text-sm border border-red-200 overflow-x-auto">
{`{
  "success": false,
  "error": "Insufficient balance",
  "code": "INSUFFICIENT_BALANCE"
}`}
        </pre>
        <div className="mt-4 space-y-2 text-sm">
          <p><strong>Common Error Codes:</strong></p>
          <ul className="list-disc list-inside space-y-1 text-slate-600 ml-4">
            <li><code>INSUFFICIENT_BALANCE</code> - Not enough balance for the transaction</li>
            <li><code>INVALID_PHONE</code> - Phone number format is invalid</li>
            <li><code>INVALID_NETWORK</code> - Network provider not supported</li>
            <li><code>PLAN_NOT_FOUND</code> - Data plan ID does not exist</li>
            <li><code>UNAUTHORIZED</code> - Invalid or missing API key</li>
          </ul>
        </div>
      </div>

      {/* Code Examples */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Code Examples</h2>

        {/* JavaScript Example */}
        <div className="mb-6">
          <h3 className="mb-3 font-medium text-slate-900">JavaScript (Node.js)</h3>
          <pre className="rounded-lg bg-slate-100 p-4 text-sm overflow-x-auto">
{`const API_KEY = 'your_api_key_here';
const BASE_URL = 'https://your-api-domain.com/api';

async function purchaseData(network, phone, planId) {
  const response = await fetch(\`\${BASE_URL}/purchase/data\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${API_KEY}\`
    },
    body: JSON.stringify({
      network,
      phone,
      planId
    })
  });

  const result = await response.json();
  return result;
}

// Usage
purchaseData('mtn', '0501234567', 'plan_123')
  .then(result => console.log(result))
  .catch(error => console.error(error));`}
          </pre>
        </div>

        {/* PHP Example */}
        <div className="mb-6">
          <h3 className="mb-3 font-medium text-slate-900">PHP</h3>
          <pre className="rounded-lg bg-slate-100 p-4 text-sm overflow-x-auto">
{`<?php
$api_key = 'your_api_key_here';
$base_url = 'https://your-api-domain.com/api';

function purchaseData($network, $phone, $planId) {
    $url = $base_url . '/purchase/data';
    $data = json_encode([
        'network' => $network,
        'phone' => $phone,
        'planId' => $planId
    ]);

    $ch = curl_init($url);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, $data);
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'Content-Type: application/json',
        'Authorization: Bearer ' . $api_key
    ]);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

    $response = curl_exec($ch);
    curl_close($ch);

    return json_decode($response, true);
}

// Usage
$result = purchaseData('mtn', '0501234567', 'plan_123');
echo json_encode($result);
?>`}
          </pre>
        </div>

        {/* Python Example */}
        <div>
          <h3 className="mb-3 font-medium text-slate-900">Python</h3>
          <pre className="rounded-lg bg-slate-100 p-4 text-sm overflow-x-auto">
{`import requests
import json

API_KEY = 'your_api_key_here'
BASE_URL = 'https://your-api-domain.com/api'

def purchase_data(network, phone, plan_id):
    url = f"{BASE_URL}/purchase/data"
    headers = {
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {API_KEY}'
    }
    data = {
        'network': network,
        'phone': phone,
        'planId': plan_id
    }

    response = requests.post(url, headers=headers, data=json.dumps(data))
    return response.json()

# Usage
result = purchase_data('mtn', '0501234567', 'plan_123')
print(result)`}
          </pre>
        </div>
      </div>

      {/* Support */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h2 className="mb-2 text-lg font-semibold text-slate-900">Need Help?</h2>
        <p className="mb-4 text-sm text-slate-600">
          Contact our support team if you need assistance with API integration
        </p>
        <button className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          <ExternalLink size={16} />
          Contact Support
        </button>
      </div>
    </div>
  );
}